import { ArrowRight, Leaf, Quote } from "lucide-react";
import ConceptHeader from "./ConceptHeader";
import { ConceptFooter } from "./LayoutServicePro";

/**
 * "Portfolio" layout — image-heavy, gallery-led.
 * Used by visual / design-build concepts (landscaping).
 * Distinct: full-bleed hero image w/ overlay title, masonry project
 * gallery, services as horizontal scroll cards, testimonial w/ image.
 */
export default function LayoutPortfolio({ c }) {
  const t = c.theme;
  const gallery = c.images.gallery || [];
  return (
    <div style={{ background: t.bg, color: t.text, fontFamily: t.fontBody, minHeight: "100vh" }}>
      <ConceptHeader industry={c.industry} />

      {/* HERO — full-bleed image */}
      <section className="relative h-[88vh] min-h-[640px] overflow-hidden">
        <div className="absolute inset-0" style={{ background: `url(${c.images.hero}) center/cover` }}/>
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.55) 100%)" }}/>

        {/* Faux nav */}
        <div className="absolute top-0 inset-x-0">
          <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 flex items-center justify-between">
            <p style={{ fontFamily: t.fontDisplay, fontSize: 28, color: "#fff" }}>{c.name}</p>
            <nav className="hidden md:flex items-center gap-7 text-[13px]" style={{ color: "#fff", opacity: 0.85 }}>
              <span>Portfolio</span><span>Services</span><span>About</span><span>Contact</span>
              <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium"
                    style={{ background: t.accent, color: "#fff" }}>
                {c.hero.ctaPrimary}
              </span>
            </nav>
          </div>
        </div>

        {/* Hero copy */}
        <div className="absolute bottom-0 inset-x-0">
          <div className="max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
            <p className="text-[12px] uppercase tracking-[0.28em]" style={{ color: t.accent }}>{c.hero.eyebrow}</p>
            <h1 className="mt-6 leading-[1.02] max-w-3xl"
                style={{ fontFamily: t.fontDisplay, fontSize: "clamp(48px, 7vw, 100px)", fontWeight: 500, color: "#fff" }}>
              {c.hero.title}
            </h1>
            <p className="mt-8 text-lg md:text-xl leading-relaxed max-w-xl" style={{ color: "rgba(255,255,255,0.85)" }}>
              {c.hero.sub}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-medium text-[15px]"
                    style={{ background: t.accent, color: "#fff" }}>
                {c.hero.ctaPrimary} <ArrowRight size={16}/>
              </span>
              <span className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-medium text-[15px] border"
                    style={{ borderColor: "rgba(255,255,255,0.4)", color: "#fff" }}>
                {c.hero.ctaSecondary}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* PORTFOLIO GALLERY */}
      <section className="py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
            <div>
              <p className="text-[12px] uppercase tracking-[0.28em]" style={{ color: t.accent }}>Recent projects</p>
              <h2 className="mt-4 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(32px, 4vw, 56px)", fontWeight: 500 }}>
                Outdoor spaces, transformed.
              </h2>
            </div>
            <p className="text-[14px]" style={{ color: t.muted }}>Designs spanning the last 24 months.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {gallery.map((url, i) => (
              <div key={url}
                   className={`relative overflow-hidden rounded-2xl ${i === 0 || i === 4 ? "row-span-2 aspect-[3/4]" : "aspect-[4/3]"}`}
                   style={{ background: `url(${url}) center/cover, ${t.surface}` }}>
                <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity"
                     style={{ background: `linear-gradient(180deg, transparent 50%, ${t.bg}E6 100%)` }}/>
                <div className="absolute bottom-4 left-4 right-4 opacity-90">
                  <p className="text-[11px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>Project · 0{i+1}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES strip */}
      <section className="py-20 md:py-24" style={{ background: t.surface }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[12px] uppercase tracking-[0.28em]" style={{ color: t.accent }}>What we do</p>
          <h2 className="mt-4 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(32px, 4vw, 48px)", fontWeight: 500 }}>
            Designed, installed, and warranted.
          </h2>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {c.services.map((s, i) => (
              <div key={s.title} className="p-7 rounded-2xl border"
                   style={{ background: t.bg, borderColor: t.muted + "22" }}>
                <Leaf size={20} style={{ color: t.accent }}/>
                <h3 className="mt-5 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: 22 }}>{s.title}</h3>
                <p className="mt-3 text-[14px] leading-relaxed" style={{ color: t.muted }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL with image */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden"
                 style={{ background: `url(${gallery[1] || c.images.hero}) center/cover` }}/>
          </div>
          <div className="lg:col-span-7">
            <Quote size={36} style={{ color: t.accent }}/>
            <p className="mt-6 leading-tight"
               style={{ fontFamily: t.fontDisplay, fontSize: "clamp(28px, 3.6vw, 44px)", fontWeight: 500 }}>
              "{c.testimonial.quote}"
            </p>
            <p className="mt-8 text-[13px] uppercase tracking-[0.22em]" style={{ color: t.muted }}>— {c.testimonial.author}</p>
            <div className="mt-10 grid sm:grid-cols-2 gap-3">
              {c.valueProps.map((v) => (
                <div key={v} className="flex items-center gap-2 text-[14px]" style={{ color: t.muted }}>
                  <Leaf size={14} style={{ color: t.accent }}/> {v}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 md:py-28" style={{ background: t.surface }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[12px] uppercase tracking-[0.28em]" style={{ color: t.accent }}>Free design consultation</p>
          <h2 className="mt-4 leading-tight"
              style={{ fontFamily: t.fontDisplay, fontSize: "clamp(36px, 4.5vw, 64px)", fontWeight: 500 }}>
            {c.finalCta}.
          </h2>
          <div className="mt-10 flex justify-center">
            <span className="inline-flex items-center gap-2 px-9 py-4 rounded-full font-medium text-[15px]"
                  style={{ background: t.accent, color: "#fff" }}>
              {c.hero.ctaPrimary} <ArrowRight size={16}/>
            </span>
          </div>
        </div>
      </section>

      <ConceptFooter accent={t.accent} muted={t.muted} bg={t.bg}/>
    </div>
  );
}
